import React, { useEffect, useMemo, useState } from 'react'
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'react-simple-snackbar';
import { createContact, updateContact } from '../../actions/contactActions';
import useAuthUser from '../../hooks/useAuthUser';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
    backgroundColor: '#1976D2',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: 'white',
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const defaultState = { name: '', role: '', email: '', phone: '', locationIds: [], accountId: '', primary: false, userId: [] }

const inputStyle = {
  display: "block",
  padding: "1.1rem 0.75rem",
  width: "100%",
  fontSize: "0.9rem",
  lineHeight: 1.25,
  color: "#55595c",
  backgroundColor: "#fff",
  backgroundImage: "none",
  backgroundClip: "padding-box",
  borderTop: "0",
  borderRight: "0",
  borderBottom: "1px solid #eee",
  borderLeft: "0",
  borderRadius: "3px",
  transition: "all 0.25s cubic-bezier(0.4, 0, 1, 1)"
}

const AddContact = ({ setOpen, open, currentId, setCurrentId, defaultAccountId, defaultLocationId, onSaved }) => {
  const authData = useAuthUser()
  const dispatch = useDispatch()
  const contact = useSelector((state) => currentId ? state.contacts.contacts.find((c) => c._id === currentId) : null)
  const accounts = useSelector((state) => state.accounts.accounts || [])
  const locations = useSelector((state) => state.locations.locations || [])
  const [contactData, setContactData] = useState(defaultState)
  // eslint-disable-next-line
  const [openSnackbar, closeSnackbar] = useSnackbar()

  const locationNameById = useMemo(() => {
    const map = {}
    locations.forEach((l) => { map[l._id] = l.name })
    return map
  }, [locations])

  useEffect(() => {
    if (contact) {
      const normalizedLocationIds = Array.isArray(contact.locationIds) && contact.locationIds.length
        ? contact.locationIds
        : (contact.locationId ? [contact.locationId] : [])
      setContactData({ ...contact, locationIds: normalizedLocationIds })
    }
  }, [contact])

  useEffect(() => {
    if (open && !currentId) {
      setContactData((prev) => ({
        ...defaultState,
        userId: prev.userId,
        accountId: defaultAccountId || '',
        locationIds: defaultLocationId ? [defaultLocationId] : [],
      }))
    }
  }, [open, currentId, defaultAccountId, defaultLocationId])

  useEffect(() => {
    const userId = authData?.result?._id || authData?.result?.googleId
    if (userId) {
      setContactData((prev) => ({ ...prev, userId: [userId] }))
    }
  }, [authData])

  const clear = () => {
    setCurrentId(null)
    setContactData(defaultState)
  }

  const handleClose = () => {
    setOpen(false);
    clear();
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    const userId = authData?.result?._id || authData?.result?.googleId
    const locationIds = Array.isArray(contactData.locationIds) ? contactData.locationIds.filter(Boolean) : []
    const payload = {
      ...contactData,
      userId: contactData.userId && contactData.userId.length ? contactData.userId : (userId ? [userId] : []),
      accountId: contactData.accountId || defaultAccountId || '',
      locationIds: locationIds.length ? locationIds : (defaultLocationId ? [defaultLocationId] : []),
      locationId: (locationIds.length ? locationIds[0] : null) || defaultLocationId || '',
    }
    let saved = null
    if (currentId) {
      saved = await dispatch(updateContact(currentId, payload, openSnackbar))
    } else {
      saved = await dispatch(createContact(payload, openSnackbar))
    }
    if (saved && typeof onSaved === 'function') onSaved(saved)
    handleClose()
  }

  return (
    <Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open} fullWidth>
      <DialogTitle id="customized-dialog-title" onClose={handleClose} style={{ paddingLeft: '20px', color: 'white' }}>
        {currentId ? 'Edit Contact' : 'Add Contact'}
      </DialogTitle>
      <DialogContent dividers>
        <div className="customInputs">
          <input
            placeholder="Name"
            style={inputStyle}
            name='name'
            type='text'
            onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
            value={contactData.name}
          />
          <input
            placeholder="Role"
            style={inputStyle}
            name='role'
            type='text'
            onChange={(e) => setContactData({ ...contactData, role: e.target.value })}
            value={contactData.role}
          />
          <input
            placeholder="Email"
            style={inputStyle}
            name='email'
            type='email'
            onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
            value={contactData.email}
          />
          <input
            placeholder="Phone"
            style={inputStyle}
            name='phone'
            type='text'
            onChange={(e) => setContactData({ ...contactData, phone: e.target.value })}
            value={contactData.phone}
          />
          <select
            style={inputStyle}
            name='accountId'
            onChange={(e) => setContactData({ ...contactData, accountId: e.target.value })}
            value={contactData.accountId || ''}
          >
            <option value="">Account (optional)</option>
            {accounts.map((a) => (
              <option key={a._id} value={a._id}>{a.name}</option>
            ))}
          </select>
          <FormControl style={{ width: '100%', marginTop: 12 }}>
            <InputLabel id="contact-locations-label">Locations (optional)</InputLabel>
            <Select
              labelId="contact-locations-label"
              multiple
              value={contactData.locationIds || []}
              displayEmpty
              onChange={(e) => setContactData({ ...contactData, locationIds: e.target.value })}
              renderValue={(selected) => {
                const ids = Array.isArray(selected) ? selected : []
                if (!ids.length) return 'Locations (optional)'
                return ids.map((id) => locationNameById[id]).filter(Boolean).join(', ')
              }}
            >
              {locations.map((l) => (
                <MenuItem key={l._id} value={l._id}>
                  <Checkbox checked={(contactData.locationIds || []).indexOf(l._id) > -1} />
                  <ListItemText primary={l.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <input
              type="checkbox"
              checked={Boolean(contactData.primary)}
              onChange={(e) => setContactData({ ...contactData, primary: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            Primary Contact
          </label>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} variant="contained" color="primary" style={{ marginRight: '16px' }}>
          Save Contact
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddContact
